/**
 * Schnittstelle zum Rechen-Worker (mathWorker.ts). Die Compute Engine ist
 * groß und wird erst geladen, wenn zum ersten Mal in einer Formel gerechnet
 * wird – wer nie rechnet, lädt sie nie.
 */

export type MathResult =
  /**
   * Zahlenwert: `exact` (Bruch, Wurzel, ganze Zahl …) und, falls verschieden,
   * gerundet. `rounded` = `exact` ist selbst schon gerundet (√2, 1/3 als
   * Dezimalzahl) – eingefügt wird dann mit „≈" statt „=".
   */
  | { ok: true; kind: "value"; exact: string; approx?: string; rounded?: boolean }
  /**
   * Gleichung mit genau einer Unbekannten. Leere `solutions` = keine reelle
   * Lösung; `all` = jede Zahl löst sie (z. B. 2x+1 = 2x+1).
   */
  | { ok: true; kind: "solve"; variable: string; solutions: string[]; all?: boolean }
  /** Gleichungssystem (`\begin{cases}…`): je Lösung eine Belegung wie ["x = 60", "y = 10"]. */
  | { ok: true; kind: "system"; solutions: string[][] }
  /** Term mit Variablen, vereinfacht oder ausmultipliziert. */
  | { ok: true; kind: "simplify"; exact: string }
  | { ok: false };

/**
 * Großzügig: der erste Aufruf lädt die Bibliothek (mehrere MB). Bleibt eine
 * Rechnung danach hängen, wird der Worker ersetzt statt weiter zu blockieren.
 */
const TIMEOUT_MS = 8_000;

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, (result: MathResult) => void>();

function reset(): void {
  worker?.terminate();
  worker = null;
  for (const [, resolve] of pending) resolve({ ok: false });
  pending.clear();
}

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("./mathWorker.ts", import.meta.url), { type: "module" });
  worker.onmessage = (e: MessageEvent<{ id: number; result: MathResult }>) => {
    pending.get(e.data.id)?.(e.data.result);
    pending.delete(e.data.id);
  };
  // Modul-Worker starten in manchen WebViews gar nicht erst, ohne Fehlermeldung
  // (siehe DocumentViewer.ts) – das Zeitlimit unten fängt auch das ab.
  worker.onerror = reset;
  return worker;
}

export function computeLatex(latex: string): Promise<MathResult> {
  const id = nextId++;
  return new Promise<MathResult>((resolve) => {
    const timer = window.setTimeout(() => {
      if (pending.has(id)) reset();
    }, TIMEOUT_MS);
    pending.set(id, (result) => {
      window.clearTimeout(timer);
      resolve(result);
    });
    try {
      getWorker().postMessage({ id, latex });
    } catch {
      window.clearTimeout(timer);
      pending.delete(id);
      resolve({ ok: false });
    }
  });
}
